import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth, api, useTenant } from '../App';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
  LayoutDashboard, 
  MapPin, 
  BarChart3, 
  Users, 
  Building2, 
  Truck, 
  FileText, 
  DollarSign, 
  Activity,
  LogOut,
  Plus,
  ChevronDown,
  Settings,
  Bell,
  Crown,
  Menu,
  X,
  MessageSquare,
  Filter,
  BookOpen,
  Wrench
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import { TooltipProvider } from './ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const roleLabels = {
  mestre: 'Master',
  adm: 'Administrador',
  financeiro: 'Financeiro',
  gestor_operacional: 'Gestor Operacional',
  operacional: 'Operacional',
  api_access: 'Acesso API'
};

const planLimits = {
  per_mission: { name: 'Por Missão', limit: 1 },
  basic: { name: 'Básico', limit: 10 },
  essential: { name: 'Essencial', limit: 50 },
  pro: { name: 'Pro', limit: 100 },
  mestre: { name: 'Ilimitado', limit: 999 }
};

const Layout = ({ children }) => {
  const { user, logout, hasRole, token } = useAuth();
  const { selectedTenant, setSelectedTenant, tenants, loadTenants } = useTenant();
  const navigate = useNavigate();
  const [cadastroOpen, setCadastroOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [notificationItems, setNotificationItems] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [mockModeActive, setMockModeActive] = useState(false);
  const [whatsappStatus, setWhatsappStatus] = useState(null);

  // Simular licença do usuário
  const userPlan = user?.role === 'mestre' ? 'mestre' : 'essential';
  const planInfo = planLimits[userPlan];
  const usedMissions = 37; // Simulado
  const remainingMissions = planInfo.limit - usedMissions;
  const usagePercent = (usedMissions / planInfo.limit) * 100;

  // Carregar tenants se for Master (apenas uma vez)
  useEffect(() => {
    if (user?.role === 'mestre' && token && tenants.length === 0) {
      loadTenants(token);
    }
  }, [user?.role, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Verificar status do WhatsApp e mensagens não lidas
  useEffect(() => {
    // WhatsApp removed - chat is now in-platform
  }, [token]);

  // Notificações reais: prestadores responderam / enviaram mensagem / concluíram etapa
  const prevTotalRef = useRef(0);
  const canSeeNotifications = hasRole(['mestre', 'adm', 'gestor_operacional', 'operacional']);

  useEffect(() => {
    if (!canSeeNotifications || !token) return;

    const checkNotifications = async () => {
      try {
        const data = await api.get('/notifications/summary', token);
        const items = data.items || [];
        const newTotal = items.length;
        if (newTotal > prevTotalRef.current && newTotal > 0) {
          try {
            const audio = new Audio('data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YU');
            audio.volume = 0.3;
            audio.play().catch(() => {});
          } catch (e) {}
        }
        prevTotalRef.current = newTotal;
        setNotificationItems(items);
      } catch (e) { /* silent */ }
    };

    checkNotifications();
    const interval = setInterval(checkNotifications, 8000);
    return () => clearInterval(interval);
  }, [canSeeNotifications, token]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNotificationClick = (item) => {
    if (item.type === 'responses') {
      navigate(`/missions/${item.mission_id}?selectSupplier=true`);
    } else {
      navigate(`/missions/${item.mission_id}`);
    }
  };

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Página Inicial', roles: ['mestre', 'adm', 'financeiro', 'gestor_operacional', 'operacional'] },
    { to: '/missions', icon: MapPin, label: 'Missões', roles: ['mestre', 'adm', 'financeiro', 'gestor_operacional', 'operacional'] },
    { to: '/indicators', icon: BarChart3, label: 'Indicadores', roles: ['mestre', 'adm', 'gestor_operacional', 'operacional'] },
    { to: '/billing', icon: DollarSign, label: 'Faturamento', roles: ['mestre', 'adm', 'financeiro'] },
    { to: '/monitoring', icon: Activity, label: 'Monitoramento', roles: ['mestre'] },
    { to: '/tenants', icon: Building2, label: 'Organizações', roles: ['mestre'] },
    { to: '/documentacao-api', icon: BookOpen, label: 'API Externa', roles: ['mestre'] },
  ];

  const cadastroItems = [
    { to: '/users', icon: Users, label: 'Usuários', roles: ['mestre', 'adm'] },
    { to: '/suppliers', icon: Building2, label: 'Fornecedores', roles: ['mestre', 'adm', 'financeiro'] },
    { to: '/prestadores', icon: Wrench, label: 'Prestadores', roles: ['mestre', 'adm', 'gestor_operacional'] },
    { to: '/customers', icon: Building2, label: 'Clientes', roles: ['mestre', 'adm', 'financeiro'] },
    { to: '/carriers', icon: Truck, label: 'Transportadores', roles: ['mestre', 'adm'] },
    { to: '/reasons', icon: FileText, label: 'Motivos', roles: ['mestre', 'adm'] },
  ];

  const filteredNavItems = navItems.filter(item => hasRole(item.roles));
  const filteredCadastroItems = cadastroItems.filter(item => hasRole(item.roles));

  // Layout simplificado para usuários api_access
  if (user?.role === 'api_access') {
    return (
      <div className="min-h-screen bg-[#F5F5F7]">
        {/* Header simplificado */}
        <header className="bg-white border-b border-slate-100 px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-900">PR APP - API Externa</h1>
                <p className="text-xs text-slate-500">Documentação e Integração</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium text-slate-700">{user?.name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
              </div>
              <Badge className="bg-indigo-100 text-indigo-700">API Access</Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  logout();
                  navigate('/login-api');
                }}
                className="text-slate-600"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sair
              </Button>
            </div>
          </div>
        </header>
        
        {/* Conteúdo */}
        <main className="max-w-7xl mx-auto p-6">
          {children}
        </main>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="min-h-screen flex bg-[#F5F5F7]">
        {/* Hamburger Menu Button - sempre visível quando menu fechado */}
        {!isExpanded && (
          <div className="fixed left-4 top-4 z-50 flex items-center gap-2">
            <button
              onClick={() => setIsExpanded(true)}
              className="w-12 h-12 bg-white rounded-xl shadow-lg flex items-center justify-center hover:bg-slate-50 transition-all hover:scale-105 border border-slate-100"
              data-testid="menu-toggle-btn"
            >
              <Menu className="w-6 h-6 text-slate-700" />
            </button>
          </div>
        )}

        {/* Overlay escuro quando menu aberto */}
        {isExpanded && (
          <div 
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 transition-opacity"
            onClick={() => setIsExpanded(false)}
          />
        )}

        {/* Sidebar - Retrátil com clique */}
        <aside 
          className={`fixed left-0 top-0 h-full bg-white flex flex-col transition-all duration-300 ease-in-out border-r border-slate-100 z-50 ${
            isExpanded ? 'w-64 translate-x-0 shadow-2xl' : 'w-64 -translate-x-full'
          }`}
        >
          {/* Header com botão fechar */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center flex-shrink-0">
                <Truck className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-xl font-bold text-slate-900 tracking-tight whitespace-nowrap" style={{ fontFamily: 'Public Sans' }}>
                PR APP
              </h1>
            </div>
            <button
              onClick={() => setIsExpanded(false)}
              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-slate-100 transition-colors"
              data-testid="menu-close-btn"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          {/* New Mission Button */}
          <div className="p-3">
            <Button 
              onClick={() => navigate('/missions/new')}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-xl h-11 btn-premium"
              data-testid="new-mission-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Solicitar Missão
            </Button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 px-2">
            <nav className="space-y-1 py-2">
              {filteredNavItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 text-sm font-medium rounded-xl mx-1 transition-all duration-200 ${
                      isActive 
                        ? 'text-slate-900 bg-slate-100 shadow-sm' 
                        : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
                    }`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  <span className="whitespace-nowrap">{item.label}</span>
                </NavLink>
              ))}

              {/* Cadastro Collapsible */}
              {filteredCadastroItems.length > 0 && (
                <Collapsible open={cadastroOpen} onOpenChange={setCadastroOpen}>
                  <CollapsibleTrigger className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-slate-500 hover:text-slate-900 hover:bg-slate-50 rounded-xl mx-1 w-full justify-between transition-all duration-200">
                    <div className="flex items-center gap-3">
                      <Settings className="w-5 h-5" />
                      <span>Cadastro</span>
                    </div>
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${cadastroOpen ? 'rotate-180' : ''}`} />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pl-4 space-y-1 mt-1">
                    {filteredCadastroItems.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-xl mx-1 transition-all duration-200 ${
                            isActive 
                              ? 'text-slate-900 bg-slate-100' 
                              : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
                          }`
                        }
                        data-testid={`nav-${item.label.toLowerCase()}`}
                      >
                        <item.icon className="w-4 h-4" />
                        <span>{item.label}</span>
                      </NavLink>
                    ))}
                  </CollapsibleContent>
                </Collapsible>
              )}
            </nav>
          </ScrollArea>

          {/* License Info — ocultado temporariamente (retornará futuramente) */}
          {false && (
          <div className="px-3 py-3 border-t border-slate-100">
            <div className="p-3 rounded-xl bg-slate-50">
              <div className="flex items-center gap-2 mb-2">
                <Crown className="w-4 h-4 text-amber-500" />
                <span className="text-xs font-medium text-slate-700">Plano {planInfo.name}</span>
              </div>
              <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
                <span>Missões usadas</span>
                <span className="font-mono">{usedMissions}/{planInfo.limit === 999 ? '∞' : planInfo.limit}</span>
              </div>
              <Progress value={Math.min(usagePercent, 100)} className="h-1.5" />
              {planInfo.limit !== 999 && (
                <p className="text-xs text-slate-400 mt-1.5">
                  {remainingMissions > 0 ? `${remainingMissions} restantes` : 'Limite atingido'}
                </p>
              )}
            </div>
          </div>
          )}

          {/* User Section */}
          <div className="p-3 border-t border-slate-100">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className="flex items-center gap-3 p-2 rounded-xl hover:bg-slate-50 transition-colors w-full"
                  data-testid="user-menu-btn"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center text-white font-medium text-sm shadow-sm flex-shrink-0">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                    <p className="text-xs text-slate-400">{roleLabels[user?.role] || user?.role}</p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-xl">
                <DropdownMenuLabel>Minha Conta</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-slate-500 text-sm">
                  {user?.email}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </aside>

        {/* Main Content - usa toda a largura */}
        <main className="flex-1 overflow-auto relative w-full">
          
          {/* Tenant Info for non-Master users */}
          {user?.role !== 'mestre' && user?.tenant_cnpj && (
            <div className="bg-slate-100 px-4 py-2 flex items-center gap-2 text-sm text-slate-600 border-b">
              <Building2 className="w-4 h-4" />
              <span>Organização: <strong>{user.tenant_name || user.tenant_cnpj}</strong></span>
            </div>
          )}
          
          <div className="p-8">
            {children}
          </div>

          {/* Floating Notification Bell - always visible for gestor roles */}
          {canSeeNotifications && (
            <div className="fixed bottom-6 right-6 z-50" data-testid="notification-bell-wrapper">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={`relative flex items-center justify-center w-14 h-14 bg-slate-900 text-white rounded-full shadow-lg hover:bg-slate-800 transition-all hover:scale-105 ${notificationItems.length > 0 ? 'animate-pulse' : ''}`}
                    data-testid="notification-bell-btn"
                  >
                    <Bell className="w-6 h-6" />
                    {notificationItems.length > 0 && (
                      <span className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center" data-testid="notification-bell-count">
                        {notificationItems.length}
                      </span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto rounded-xl p-2" data-testid="notification-bell-dropdown">
                  <DropdownMenuLabel className="text-slate-900">
                    Notificações
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {notificationItems.length === 0 ? (
                    <div className="p-4 text-center text-sm text-slate-400">Nenhuma notificação pendente</div>
                  ) : (
                    notificationItems.map((item, idx) => (
                      <DropdownMenuItem
                        key={`${item.type}-${item.mission_id}-${idx}`}
                        onClick={() => handleNotificationClick(item)}
                        className="flex items-center justify-between gap-2 p-3 rounded-lg cursor-pointer"
                        data-testid={`notification-item-${idx}`}
                      >
                        <div className="min-w-0">
                          <p className="font-mono text-sm font-medium">{item.code}</p>
                          <p className="text-xs text-slate-500 truncate">{item.text}</p>
                        </div>
                        <Badge className={`flex-shrink-0 ${item.type === 'responses' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                          {item.count > 1 ? `+${item.count}` : item.type === 'responses' ? `${item.count}` : 'novo'}
                        </Badge>
                      </DropdownMenuItem>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </main>
      </div>
    </TooltipProvider>
  );
};

export default Layout;
